import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useMasterData } from '../../hooks/useMasterData';
import { CogIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useTextInput } from '../../hooks/useTextInput';
import { useModal } from '../../hooks/useModal';

interface ManufacturerSelectorProps {
  value: string;
  onChange: (manufacturerId: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  error?: string;
}

interface ManufacturerOption {
  id: string;
  name: string;
  isOtherOption?: boolean; // 「その他」オプションかどうかのフラグ
}

export const ManufacturerSelector: React.FC<ManufacturerSelectorProps> = ({
  value,
  onChange,
  placeholder = 'メーカーを選択してください',
  label,
  required = false,
  disabled = false,
  className = '',
  error,
}) => {
  const { manufacturers } = useMasterData();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 統一入力処理フック（検索クエリ用）
  const searchInput = useTextInput({
    initialValue: searchQuery,
    onValueChange: value => {
      setSearchQuery(value);
    },
  });

  // 統一モーダル管理フック（ドロップダウン用）
  useModal({
    isOpen: isOpen,
    onClose: () => {
      setIsOpen(false);
      setSearchQuery('');
      searchInput.setValue('');
    },
    containerRef: dropdownRef,
    closeOnOverlayClick: true,
    closeOnEscape: true,
  });

  // メーカーオプション（「その他」が既にmanufacturersに含まれている場合は重複追加しない）
  const manufacturerOptions = useMemo((): ManufacturerOption[] => {
    const options: ManufacturerOption[] = [];

    // 既存のメーカーを追加
    manufacturers.forEach(manufacturer => {
      options.push({
        id: manufacturer.id,
        name: manufacturer.name,
      });
    });

    // 「その他」が既に含まれていない場合のみ追加
    const hasOtherManufacturer = manufacturers.some(m => m.name === 'その他');
    if (!hasOtherManufacturer) {
      options.push({
        id: 'other',
        name: 'その他',
        isOtherOption: true,
      });
    }

    // 名前順にソート（「その他」は最後に表示）
    return options.sort((a, b) => {
      if (a.isOtherOption && !b.isOtherOption) return 1;
      if (!a.isOtherOption && b.isOtherOption) return -1;
      return a.name.localeCompare(b.name, 'ja');
    });
  }, [manufacturers]);

  // 検索フィルタリング
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return manufacturerOptions;

    const query = searchQuery.toLowerCase();
    return manufacturerOptions.filter(option => option.name.toLowerCase().includes(query));
  }, [manufacturerOptions, searchQuery]);

  // 現在選択されているオプションを取得
  const selectedOption = useMemo(() => {
    return manufacturerOptions.find(option => option.id === value);
  }, [value, manufacturerOptions]);

  const handleOptionSelect = useCallback(
    (option: ManufacturerOption) => {
      onChange(option.id);
      setIsOpen(false);
      setSearchQuery('');
      searchInput.setValue(''); // 統一入力フックもリセット
    },
    [onChange, searchInput]
  );

  const handleClear = () => {
    onChange('');
    setIsOpen(false);
    setSearchQuery('');
    searchInput.setValue(''); // 統一入力フックもリセット
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
          {label}
          {required && <span className='text-red-500 ml-1'>*</span>}
        </label>
      )}

      <div className='relative' ref={dropdownRef}>
        {/* セレクタートリガー */}
        <div className='relative'>
          <button
            type='button'
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className={`
                          w-full px-4 py-3 text-left bg-gray-50 dark:bg-gray-700 border rounded-lg shadow-sm 
                          focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-primary-500
                          min-h-[44px] touch-target-input text-gray-900 dark:text-white
                          ${!error ? 'border-gray-300 dark:border-gray-600' : 'border-red-300 dark:border-red-600'}
                          ${disabled ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'hover:border-gray-400 dark:hover:border-gray-500'}
                          transition-colors duration-200
                      `}
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center'>
                <CogIcon className='h-5 w-5 text-gray-400 mr-3' />
                <span
                  className={
                    selectedOption
                      ? 'text-gray-900 dark:text-white'
                      : 'text-gray-500 dark:text-gray-400'
                  }
                >
                  {selectedOption ? selectedOption.name : placeholder}
                </span>
              </div>
              <ChevronDownIcon
                className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </button>
        </div>

        {/* ドロップダウンメニュー */}
        {isOpen && !disabled && (
          <div className='absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-80 overflow-hidden'>
            {/* 検索ボックス */}
            <div className='p-3 border-b border-gray-200 dark:border-gray-700'>
              <div className='relative'>
                <MagnifyingGlassIcon className='h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2' />
                <input
                  type='text'
                  {...searchInput.inputProps}
                  placeholder='メーカーを検索...'
                  className='w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                />
              </div>
            </div>

            {/* オプションリスト */}
            <div className='max-h-64 overflow-y-auto'>
              {/* クリアオプション */}
              {selectedOption && (
                <button
                  type='button'
                  onClick={handleClear}
                  className='w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'
                >
                  選択をクリア
                </button>
              )}

              {/* メーカーオプション */}
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => (
                  <button
                    key={option.id}
                    type='button'
                    onClick={() => handleOptionSelect(option)}
                    className={`
                                        w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                                        ${
                                          selectedOption?.id === option.id
                                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                                            : 'text-gray-900 dark:text-white'
                                        }
                                    `}
                  >
                    <div className='flex items-center'>
                      <CogIcon className='h-4 w-4 text-gray-400 mr-3' />
                      <div>
                        <div
                          className={`font-medium ${option.isOtherOption ? 'text-blue-600 dark:text-blue-400' : ''}`}
                        >
                          {option.name}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className='px-4 py-3 text-gray-500 dark:text-gray-400 text-center'>
                  {searchQuery ? '検索結果が見つかりません' : '利用可能なメーカーがありません'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{error}</p>}
    </div>
  );
};

export default ManufacturerSelector;
